import { useMemo, useEffect } from "react";
import { Navigate, useParams, useSearchParams } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { Card, Space, Spin, Tabs } from "antd";

import { getUser } from "../../../services/auth";
import useDeviceType from "../../../utils/deviceType";
import { canUserAccessFile } from "../../../utils/filePermissions";
import { getFileById } from "../api/fileAPI";
import { FileDetail } from "../components/FileDetail";
import { FileProfit } from "../components/FileProfit";
import FileStatus from "../components/FileStatus";

const DetailFile = () => {
  const user = getUser();
  const userRole = user.role;
  const { isMobile } = useDeviceType();
  const { id } = useParams();
  const fileId = id;
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab") || "1";

  const {
    data: fileData,
    refetch: refetchFile,
    isLoading,
  } = useQuery({
    queryKey: ["file", fileId],
    queryFn: () => getFileById(fileId),
    enabled: !!fileId,
  });

  const showFileProfit =
    userRole === "admin" ||
    (userRole === "branchAdmin" && user.branch?.branchName === "Seremban");

  // Get valid tab keys and validate/correct tab parameter in one memo
  const activeKeyTab = useMemo(() => {
    const validTabKeys = ["1", "2"];
    if (showFileProfit) {
      validTabKeys.push("3");
    }

    // Validate and return correct tab
    if (validTabKeys.includes(tabFromUrl)) {
      return tabFromUrl;
    }
    return "1"; // Default to first tab if invalid
  }, [tabFromUrl, showFileProfit]);

  // Update URL if invalid tab was provided
  useEffect(() => {
    if (tabFromUrl !== activeKeyTab) {
      setSearchParams({ tab: activeKeyTab }, { replace: true });
    }
  }, [tabFromUrl, activeKeyTab, setSearchParams]);

  // Check if user has permission to access this file
  const hasAccess = useMemo(() => {
    if (!fileData || isLoading) return false;
    return canUserAccessFile(fileData);
  }, [fileData, isLoading]);

  const onChangeTab = (key: string) => {
    setSearchParams({ tab: key });
  };

  if (!fileId) {
    return <Navigate to="/404" replace />;
  }

  // Redirect to files list if user doesn't have access
  if (fileData && !isLoading && !hasAccess) {
    return <Navigate to="/files" replace />;
  }

  const items = [
    {
      key: "1",
      label: "File Details",
      children: (
        <Card
          style={{
            border: "1px solid #f0f0f0",
            borderTop: "none",
            borderRadius: 8,
            borderTopLeftRadius: 0,
            boxShadow: "none",
          }}
        >
          <FileDetail
            fileData={fileData}
            isMobile={isMobile}
            userRole={userRole}
            canAccess={hasAccess}
          />
        </Card>
      ),
    },
    {
      key: "2",
      label: "File Status",
      children: (
        <Card
          style={{
            border: "1px solid #f0f0f0",
            borderTop: "none",
            borderRadius: 8,
            borderTopLeftRadius: 0,
            boxShadow: "none",
          }}
        >
          <FileStatus
            fileId={fileId}
            userRole={userRole}
            fileData={fileData}
            isLoadingFileData={isLoading}
            refetchFile={refetchFile}
          />
        </Card>
      ),
    },
    ...(showFileProfit
      ? [
          {
            key: "3",
            label: "File Profit",
            children: (
              <Card
                style={{
                  border: "1px solid #f0f0f0",
                  borderTop: "none",
                  borderRadius: 8,
                  borderTopLeftRadius: 0,
                  boxShadow: "none",
                }}
              >
                <FileProfit fileData={fileData} refetchFile={refetchFile} />
              </Card>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      {isLoading ? (
        <Spin fullscreen />
      ) : (
        <Space
          direction="vertical"
          style={{ margin: 10, width: "-webkit-fill-available" }}
        >
          <Tabs
            activeKey={activeKeyTab}
            onChange={onChangeTab}
            type="card"
            items={items}
            tabBarStyle={{ margin: 0 }}
          />
        </Space>
      )}
    </>
  );
};

export default DetailFile;
